import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Users, FolderKanban, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  TaskWorkspace,
  type ViewId,
  type TaskViewId,
  getDefaultViewId,
  isTaskViewId,
  isValidViewId,
} from '@/components/workspace';
import { CreateTaskDialog } from '@/features/tasks';
import { useProject } from '@/hooks/use-projects';
import { useTasks } from '@/hooks/use-tasks';
import { useLists, useListStatuses } from '@/hooks/use-lists';
import { useProjectMembers } from '@/hooks/use-project-members';
import { useProjectChats } from '@/hooks/use-chats';
import { useSocket } from '@/hooks/use-socket';
import { useAuth } from '@/hooks/use-auth';
import { useMyCapabilities } from '@/hooks/use-permissions';
import { useUIStore } from '@/stores/ui-store';
import { useSetViewPreference, useViewPreference } from '@/hooks/use-view-preference';
import type { TaskVisibilityScope } from '@/lib/api';

export function ProjectBoardPage() {
  const { projectId, listId } = useParams<{ projectId: string; listId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewFromUrl = searchParams.get('view');

  const { data: project, isLoading: isProjectLoading, error } = useProject(projectId);
  const { data: lists = [], isLoading: listsLoading } = useLists(projectId);
  const { data: chats = [] } = useProjectChats(projectId || '');

  const { data: savedPreference, isLoading: isLoadingPreference } = useViewPreference(
    projectId && listId
      ? {
          scopeType: 'list',
          scopeId: listId,
          viewType: 'list',
        }
      : null,
  );
  const setViewPreference = useSetViewPreference();

  const currentView: ViewId = useMemo(() => {
    if (viewFromUrl && isValidViewId(viewFromUrl)) {
      return viewFromUrl as ViewId;
    }
    const savedView = savedPreference?.preferences?.selectedView;
    if (!isLoadingPreference && typeof savedView === 'string' && isValidViewId(savedView)) {
      return savedView as ViewId;
    }
    return getDefaultViewId();
  }, [viewFromUrl, savedPreference, isLoadingPreference]);

  const { data: members = [] } = useProjectMembers(projectId);
  const { data: effectiveStatuses = [], isLoading: statusesLoading } = useListStatuses(listId);
  const { joinProject, leaveProject } = useSocket();
  const { user } = useAuth();
  const { openCreateTask } = useUIStore();
  const { data: capabilities } = useMyCapabilities();
  const [taskVisibilityScope, setTaskVisibilityScope] = useState<TaskVisibilityScope>('all');

  const isClient = user?.role === 'client';
  const canCreateTask = Boolean(capabilities?.['task.create']);
  const { data: tasks = [], isLoading: tasksLoading } = useTasks(projectId, listId, {
    visibilityScope: taskVisibilityScope,
  });

  /** Visibility scope is not persisted — reset to All active when the list/project route changes. */
  const routeKey = `${projectId ?? ''}:${listId ?? ''}`;
  const prevRouteKeyRef = useRef<string | null>(null);
  if (prevRouteKeyRef.current !== routeKey) {
    const hadPriorRoute = prevRouteKeyRef.current !== null;
    prevRouteKeyRef.current = routeKey;
    if (hadPriorRoute) {
      setTaskVisibilityScope('all');
    }
  }

  const handleTaskVisibilityScopeChange = useCallback((scope: TaskVisibilityScope) => {
    setTaskVisibilityScope(scope);
  }, []);

  const handleViewChange = useCallback(
    (viewId: ViewId) => {
      const next = new URLSearchParams(searchParams);
      next.set('view', viewId);
      setSearchParams(next, { replace: true });

      if (projectId && listId && isTaskViewId(viewId)) {
        setViewPreference.mutate({
          scopeType: 'list',
          scopeId: listId,
          viewType: 'list',
          preferences: { selectedView: viewId as TaskViewId },
        });
      }
    },
    [searchParams, setSearchParams, projectId, listId, setViewPreference],
  );

  useEffect(() => {
    if (projectId) {
      joinProject(projectId);
      return () => leaveProject(projectId);
    }
  }, [projectId, joinProject, leaveProject]);

  useEffect(() => {
    const savedView = savedPreference?.preferences?.selectedView;
    if (!viewFromUrl && typeof savedView === 'string' && isValidViewId(savedView)) {
      const next = new URLSearchParams(searchParams);
      next.set('view', savedView);
      setSearchParams(next, { replace: true });
    }
  }, [savedPreference, viewFromUrl, searchParams, setSearchParams]);

  const selectedList = lists.find((item) => item.id === listId);

  if (isProjectLoading || listsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !project || !listId || !selectedList) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <FolderKanban className="h-12 w-12 text-muted-foreground/50" />
        <p className="text-muted-foreground">List not found</p>
        <Button asChild variant="outline">
          <Link to={`/projects/${projectId}`}>Back to Project Dashboard</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button asChild variant="ghost" size="icon">
                <Link to={`/projects/${project.id}`}>
                  <ChevronLeft className="h-4 w-4" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Back to Project Dashboard</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div className="flex min-w-0 items-center gap-3">
          <div className="h-4 w-4 shrink-0 rounded" style={{ backgroundColor: project.color }} />
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold">{selectedList.name}</h1>
            <p className="text-xs text-muted-foreground">{project.name}</p>
          </div>
        </div>

        <Badge variant="secondary" className="shrink-0 gap-1">
          {tasks.length} tasks
        </Badge>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="shrink-0 gap-2">
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">{project.members?.length || 0}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{project.members?.length || 0} members</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {canCreateTask ? (
          <Button size="sm" className="shrink-0 gap-2" onClick={() => openCreateTask()}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Add Task</span>
          </Button>
        ) : null}
      </div>

      <div className="flex-1 overflow-hidden">
        {statusesLoading ? (
          <div className="flex h-full items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : effectiveStatuses.length > 0 ? (
          <TaskWorkspace
            projectId={project.id}
            listId={listId}
            tasks={tasks}
            statuses={effectiveStatuses}
            members={members}
            isLoading={tasksLoading}
            isClient={isClient}
            currentView={currentView}
            onViewChange={handleViewChange}
            taskVisibilityScope={taskVisibilityScope}
            onTaskVisibilityScopeChange={handleTaskVisibilityScopeChange}
            showTaskVisibilityFilter={true}
            chats={chats}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-4">
            <FolderKanban className="h-12 w-12 text-muted-foreground/50" />
            <div className="text-center">
              <p className="text-muted-foreground">No statuses configured</p>
              <p className="text-sm text-muted-foreground/70">Add statuses to start organizing tasks</p>
            </div>
          </div>
        )}
      </div>

      {canCreateTask && effectiveStatuses.length > 0 ? (
        <CreateTaskDialog
          projectId={project.id}
          listId={listId}
          statuses={effectiveStatuses}
          projectName={project.name}
          listName={selectedList.name}
        />
      ) : null}
    </div>
  );
}
